import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { createTheme as createDSTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconCamera } from '@tabler/icons-react-native';
import type { ImagePickerAsset } from 'expo-image-picker';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, TextInput } from 'react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { SubjectCard } from './update-rating-subject-card';

const theme = createDSTheme();
const detailsTitleRowStyle = { flexWrap: 'wrap' } as const;
const addPhotoButtonStyle = {
  alignItems: 'center',
  borderColor: theme.colors['border.secondary'],
  borderRadius: 22,
  borderStyle: 'dashed',
  borderWidth: 1,
  flexDirection: 'row',
  gap: theme.spacing['spacing.general.x4'],
  minHeight: 72,
  paddingHorizontal: theme.spacing['spacing.general.x4'],
} as const;
const imageRoundedStyle = { borderRadius: 12 } as const;
const textInputStyle = {
  backgroundColor: theme.colors['surface.default'],
  borderColor: theme.colors['text.primary'],
  borderRadius: 20,
  borderWidth: 3,
  color: theme.colors['text.primary'],
  fontSize: 18,
  lineHeight: 28,
  minHeight: 150,
  padding: theme.spacing['spacing.general.x4'],
} as const;

export function UpdateRatingCommentStep({
  comment,
  commentImage,
  commentInputRef,
  commentOffset: commentOffsetRef,
  isCommentRequired,
  onChangeComment,
  onUseImagePickerCamera,
  overallExperience,
  subjectImage,
  subjectName,
  subjectSubtitle,
}: {
  comment: string;
  commentImage: ImagePickerAsset | null;
  commentInputRef: RefObject<TextInput | null>;
  commentOffset: RefObject<number>;
  isCommentRequired: boolean;
  onChangeComment: (comment: string) => void;
  onUseImagePickerCamera: () => void;
  overallExperience?: 'positive' | 'negative';
  subjectImage?: string | null;
  subjectName: string;
  subjectSubtitle: string;
}) {
  const { t } = useTranslation('ratings');

  return (
    <Animated.View entering={FadeInUp.duration(220)}>
      <Column
        gap="spacing.general.x4"
        onLayout={(event) => {
          commentOffsetRef.current = event.nativeEvent.layout.y;
        }}
      >
        <SubjectCard
          image={subjectImage}
          name={subjectName}
          recommended={overallExperience === 'positive'}
          subtitle={subjectSubtitle}
        />
        {overallExperience === 'positive' ? (
          <Column gap="spacing.general.x2">
            <Row alignItems="flex-end" gap="spacing.general.x1" style={detailsTitleRowStyle}>
              <Text color="text.primary" fontSize={22} fontWeight="700" lineHeight={30}>
                {t('newRating.figmaImproveListingTitle')}
              </Text>
              <Text color="text.secondary" fontSize={16} lineHeight={24}>
                {t('newRating.figmaOptional')}
              </Text>
            </Row>
            <Pressable onPress={onUseImagePickerCamera} style={addPhotoButtonStyle}>
              {commentImage ? (
                <PCImage
                  aspectRatio={1}
                  height={48}
                  source={{ uri: commentImage.uri }}
                  style={imageRoundedStyle}
                  width={48}
                />
              ) : (
                <IconCamera strokeWidth={ICON_STROKE_WIDTH} size={40} color={theme.colors['icon.tertiary']} />
              )}
              <Text color="text.primary" flexShrink={1} fontSize={17} fontWeight="700">
                {t(`newRating.${commentImage ? 'figmaModifyPhotoTitle' : 'figmaAddPhotoTitle'}`)}
              </Text>
            </Pressable>
          </Column>
        ) : null}
        <Column gap="spacing.general.x2">
          <Row alignItems="flex-end" gap="spacing.general.x1" style={detailsTitleRowStyle}>
            <Text color="text.primary" fontSize={22} fontWeight="700" lineHeight={30}>
              {t('newRating.figmaCommentTitle')}
            </Text>
            <Text color="text.secondary" fontSize={16} lineHeight={24}>
              {t(isCommentRequired ? 'newRating.figmaRequired' : 'newRating.figmaOptional')}
            </Text>
          </Row>
          <TextInput
            ref={commentInputRef}
            maxLength={1000}
            multiline
            onChangeText={onChangeComment}
            placeholderTextColor={theme.colors['text.tertiary']}
            style={textInputStyle}
            textAlignVertical="top"
            value={comment}
          />
          <Text color="text.secondary" fontSize={13} lineHeight={18} textAlign="center">
            {t('newRating.figmaPublicCommentHint')}
          </Text>
        </Column>
      </Column>
    </Animated.View>
  );
}
